// The "synced to Roll20?" dot on a party card. Colour and remedy come from the
// pure state/tokenSync.ts; this file is only the plumbing: one shared map read
// for every card, and the dot itself.
import { useEffect, useRef, useState } from "react";
import { useStore } from "../state/store";
import { useBridgeStatus } from "../bridge";
import { readMap, type MapRead } from "../state/auraReach";
import { activeRoll20Link, storedBinding, tokenSyncStatus, type SyncLevel } from "../state/tokenSync";
import type { Character } from "../state/types";

const GLYPH: Record<SyncLevel, string> = { green: "●", yellow: "●", red: "●", grey: "○" };

/** ONE Roll20 map read, shared by every party card and refreshed while the
 *  Party overview is open. A single poller — not one per card — so the dots do
 *  not add N map reads per tick to the map-tick load the codebase is already
 *  trying to coalesce. Polls only when a game is linked and the connector can
 *  read tokens; pauses while the tab is hidden. */
export function useRoll20MapSnapshot(): { map: MapRead | null; checking: boolean; recheck: () => void } {
  const { layout } = useStore();
  const bridge = useBridgeStatus();
  const link = activeRoll20Link(layout);
  const canRead = Boolean(link) && Boolean(bridge.capabilities?.roll20TokenBars);
  const [map, setMap] = useState<MapRead | null>(null);
  const [checking, setChecking] = useState(false);
  const busy = useRef(false);
  const run = async () => {
    const l = activeRoll20Link(layout);
    if (!l || !canRead || busy.current) return;
    busy.current = true; setChecking(true);
    try { setMap(await readMap(l)); } catch { /* keep the last snapshot rather than flicker to grey */ }
    finally { busy.current = false; setChecking(false); }
  };
  const runRef = useRef(run); runRef.current = run;
  // Re-key on the linked tab so a campaign switch or a Roll20 re-link re-reads.
  const key = canRead && link ? `${link.tabId}:${link.frameId}` : "";
  useEffect(() => {
    if (!key) { setMap(null); return; }
    let alive = true;
    void runRef.current();
    const timer = window.setInterval(() => { if (!document.hidden && alive) void runRef.current(); }, 15000);
    return () => { alive = false; window.clearInterval(timer); };
  }, [key]);
  return { map, checking, recheck: () => void runRef.current() };
}

/** The status dot for one party member. Hidden entirely when Roll20 was never
 *  set up for this table and this character was never bound — a row of grey dots
 *  on a Roll20-less campaign is noise, not information. */
export function TokenSyncDot({ c, map, onRecheck }: { c: Character; map: MapRead | null; onRecheck?: () => void }) {
  const { layout } = useStore();
  const linked = Boolean(activeRoll20Link(layout));
  const bound = storedBinding(layout, c.id);
  if (!linked && !bound?.tokenId) return null;
  const s = tokenSyncStatus(layout, c, map);
  const title = `Roll20 sync — ${s.label}: ${s.detail}${s.remedy ? `\n→ ${s.remedy}` : ""}`;
  return (
    <button
      type="button"
      className={`sync-dot ${s.level}`}
      title={title}
      aria-label={`Roll20 sync for ${c.name}: ${s.label}. ${s.detail}${s.remedy ? " " + s.remedy : ""}`}
      onClick={(e) => { e.stopPropagation(); onRecheck?.(); }}
    >
      <span aria-hidden="true">{GLYPH[s.level]}</span>
    </button>
  );
}
